import * as React from "react";
import { useCallback, useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { DataLake } from "@graylog/enterprise-api";
import { MenuItem } from "components/bootstrap";
import BulkActionsDropdown from "components/common/EntityDataTable/BulkActionsDropdown";
import BulkRetrievalModal from "data-lake/preview/BulkRetrievalModal";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useSelectedEntities from "components/common/EntityDataTable/hooks/useSelectedEntities";
import AddEvidenceModal from "security-app/components/Investigations/plugin/shared/AddEvidenceModal";
import useInvestigationPanel from "security-app/components/Investigations/hooks/useInvestigationPanel";
import { useGetInvestigationDetails } from "security-app/hooks/useInvestigationsAPI";
import { ConfirmDialog } from "components/common";
import useSelectedDataLake from "data-lake/hooks/useSelectedDataLake";
import useRetrieval from "data-lake/preview/hooks/useRetrieval";
import AddToInvestigationHelp from "data-lake/preview/AddToInvestigationHelp";
import useActionCondition from "security-app/components/Investigations/plugin/useActionCondition";
import { investigationDetailsQueryKey } from "security-app/hooks/queryKeys";
import useSelectableLogViewMessages from "logview/components/contexts/useSelectableLogViewMessages";
const BulkActions = ({ retrievalPayload }) => {
  const [showRetrievalModal, setShowRetrievalModal] = useState(false);
  const sendTelemetry = useSendTelemetry();
  const { selectedEntities } = useSelectedEntities();
  const { selectableLogViewMessages } = useSelectableLogViewMessages();
  const selectedMessages = selectableLogViewMessages.filter(({ id }) => selectedEntities.includes(id));
  const addEvidenceModalRef = useRef(null);
  const queryClient = useQueryClient();
  const { selectedInvestigationId, expandPanel } = useInvestigationPanel();
  const { currentInvestigation } = useGetInvestigationDetails(selectedInvestigationId);
  const [addingToInvestigation, setAddingToInvestigation] = useState(false);
  const [showAddToCurrentModal, setShowAddToCurrentModal] = useState(false);
  const { bulkRetrievePayload } = useRetrieval();
  const { selectedDataLake } = useSelectedDataLake();
  const displayInvestigationActions = useActionCondition();
  const toggleShowModal = useCallback(() => {
    if (!showRetrievalModal) {
      sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.PREVIEW[selectedDataLake.type].RETRIEVE_LOGS_BULK_ACTION_CLICK, {
        app_pathname: "/data-storage/preview",
        app_section: "log-view-widget",
        event_details: {
          messages_amount: selectedEntities.length,
          data_lake_storage_type: selectedDataLake.storageType
        }
      });
    }
    setShowRetrievalModal((cur) => !cur);
  }, [selectedDataLake.storageType, selectedDataLake.type, selectedEntities.length, sendTelemetry, showRetrievalModal]);
  const disabledAdd = !currentInvestigation || (currentInvestigation == null ? void 0 : currentInvestigation.archived);
  const updateInvestigation = useCallback(
    (investigationIds) => DataLake.addMultipleMessagesToInvestigation({
      investigation_ids: investigationIds,
      retrieval_request: bulkRetrievePayload(
        selectedMessages.map(({ id, timestamp }) => ({ id, timestamp })),
        selectedDataLake.id,
        retrievalPayload
      )
    }).then(async () => {
      await new Promise((resolve) => {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: investigationDetailsQueryKey() });
          resolve();
        }, 1e3);
      });
    }),
    [bulkRetrievePayload, queryClient, retrievalPayload, selectedDataLake.id, selectedMessages]
  );
  const onAddLog = async () => {
    setAddingToInvestigation(true);
    if (disabledAdd) return;
    await updateInvestigation([selectedInvestigationId]);
    setAddingToInvestigation(false);
    setShowAddToCurrentModal(false);
    expandPanel();
  };
  const onAddEvidence = async (payload) => updateInvestigation(payload.investigation_ids);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(BulkActionsDropdown, { bsSize: "xs" }, /* @__PURE__ */ React.createElement(MenuItem, { onSelect: toggleShowModal }, "Retrieve Logs"), displayInvestigationActions && /* @__PURE__ */ React.createElement(React.Fragment, null, disabledAdd || /* @__PURE__ */ React.createElement(MenuItem, { onClick: () => setShowAddToCurrentModal(true), icon: "extension" }, "Add to investigation"), /* @__PURE__ */ React.createElement(MenuItem, { onClick: () => addEvidenceModalRef.current.toggle(), icon: "extension" }, "Select an investigation"))), showRetrievalModal && /* @__PURE__ */ React.createElement(BulkRetrievalModal, { onCancel: toggleShowModal, retrievalPayload }), showAddToCurrentModal && /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: true,
      isAsyncSubmit: true,
      isSubmitting: addingToInvestigation,
      btnConfirmText: "Add to investigation",
      onConfirm: onAddLog,
      submitLoadingText: "Adding to investigation...",
      onCancel: () => setShowAddToCurrentModal(false),
      title: "Adding messages to current investigation"
    },
    /* @__PURE__ */ React.createElement(AddToInvestigationHelp, null)
  ), displayInvestigationActions && /* @__PURE__ */ React.createElement(
    AddEvidenceModal,
    {
      index: void 0,
      ids: selectedEntities,
      type: "logs",
      ref: addEvidenceModalRef,
      onSubmit: onAddEvidence
    },
    /* @__PURE__ */ React.createElement(AddToInvestigationHelp, null)
  ));
};
export default BulkActions;
